import { defineStore } from 'pinia';
import { useRoute } from 'vue-router';
import {
	Promotion, EnabledPromotion, SuggestedPromotion, PromotionPackage,
} from '@customer/types/Promotion';
import { useAppStore } from '@globals/stores/app';
import { useUserStore } from '@globals/stores/user';
import { useCustomerOnboardingStore } from '@customer/stores/customerOnboarding';
import serverFetch from '@globals/ServerFetch';

// eslint-disable-next-line import/prefer-default-export
export const usePromotionStore = defineStore({
	id: 'promotion',
	state: () => ({
		customerId: useAppStore().isAdminModule ? Number(useRoute()?.params?.id) : useUserStore().customerId,
		cartPromotions: [] as Promotion[],
		selectedPromotionId: 0,
		isPromotionEnabled: false,
		enabledPromotions: [] as EnabledPromotion[],
		isLimitModalVisible: false,
		isFeedbackModalVisible: false,
		isDisableRenewModalVisible: false,
		isSummaryPage: false,
		mobileScrollTopPosition: 0,
		promotionPackages: [] as PromotionPackage[],
		isServerError: false,
		isUnpaidInvoice: false,
		isNotEnoughPoints: false,
		isPromotionModalVisible: false,
		accountPoints: 0,
		enabledListsLength: 0,
		categoryInput: '',
		locationInput: '',
		selectedCategoryId: '',
		selectedLocationId: '',
		suggestions: [] as SuggestedPromotion[],
		suggestionsCheckboxFilter: false,
		apiNamespace: useAppStore().isAdminModule ? 'admin' : 'promotion',
		isFillProfileModalVisible: false,
		promotionIndicator: {
			hasGallery: false,
			hasMarks: false,
			hasDescription: false,
		},
		showConfirmationMessageBar: false,
		fromListBox: '',
		displayPhoneVerification: false,
	}),
	actions: {
		async getEnabledPromotions({ scrollEnabledList } : { scrollEnabledList: boolean }) {
			const { isAdminModule } = useAppStore();

			const url = isAdminModule ? `/admin/active-promotions.ofer?idCustomer=${this.customerId}` : '/promotion/active-promotions.ofer';

			try {
				const { result } = await serverFetch(url);

				this.enabledPromotions = [...result];

				this.enabledPromotions.forEach((promotion) => {
					promotion.isDisabled = promotion.takenPositions >= 5;
				});

				if (scrollEnabledList) {
					setTimeout(() => {
						this.scrollActivePromotionsList();
					}, 600);
				}
			} catch (error) {
				console.error(error);
			}
		},
		async getSuggestions() {
			const { isAdminModule } = useAppStore();
			let url = isAdminModule ? `/${this.apiNamespace}/get-promotion-suggestions.ofer?idCustomer=${this.customerId}&locationCategoryDTO.categoryId=${this.selectedCategoryId}`
			: `/${this.apiNamespace}/get-promotion-suggestions.ofer?locationCategoryDTO.categoryId=${this.selectedCategoryId}`;
			if (this.selectedLocationId) {
				url = isAdminModule ? `/${this.apiNamespace}/get-promotion-suggestions.ofer?locationCategoryDTO.categoryId=${this.selectedCategoryId}&locationCategoryDTO.locationIdsString=${this.selectedLocationId}`
				: `/${this.apiNamespace}/get-promotion-suggestions.ofer?locationCategoryDTO.categoryId=${this.selectedCategoryId}&locationCategoryDTO.locationIdsString=${this.selectedLocationId}`;
			}
			try {
				const { suggestions } = await serverFetch(url);
				const suggestionsWithUid = suggestions.map((e) => ({ ...e, uid: `${e.locationId}${e.categoryId}` }));
				this.suggestions = this.processSugesstions(suggestionsWithUid);
			} catch (error) {
				console.error(error);
			}
		},
		processSugesstions(suggestions: SuggestedPromotion[]) {
			this.enabledPromotions.forEach((enabledItem) => {
				enabledItem.categoryId = Number(this.selectedCategoryId);
			});

			suggestions.forEach((suggestionItem) => {
				suggestionItem.isDisabled = suggestionItem.takenPositions >= 5;
			});

			if (!this.suggestionsCheckboxFilter) {
				return suggestions;
			}

			return suggestions.filter((suggestionItem) => suggestionItem.isFromCustomerFilters);
		},
		async getPromotionRecommendations() {
			const { isAdminModule } = useAppStore();

			const url = isAdminModule ? `/${this.apiNamespace}/get-promotion-recommendations.ofer?idCustomer=${this.customerId}` : `/${this.apiNamespace}/get-promotion-recommendations.ofer`;

			try {
				const { result } = await serverFetch(url);

				const resultWithUid = result.map((e) => ({ ...e, uid: `${e.locationId}${e.categoryId}` }));
				this.suggestions = resultWithUid;
			} catch (error) {
				console.error(error);
			}
		},
		async getPromotionIndicators() {
			const { hasGallery, hasMarks, hasDescription } = await serverFetch(
				`/promotion/get-profile-promotion-indicators.ofer?idCustomer=${this.customerId}`,
			);

			this.promotionIndicator.hasGallery = hasGallery;
			this.promotionIndicator.hasMarks = hasMarks;
			this.promotionIndicator.hasDescription = hasDescription;
		},
		addToCart(item: Promotion, isPromotionsLimitReached: boolean) {
			if (item.isDisabled) {
				return;
			}

			const { isAdminModule } = useAppStore();

			if (isPromotionsLimitReached && !isAdminModule) {
				this.togglePromotionLimitModal(true);

				return;
			}

			if (!this.cartPromotions.find((cartItem) => cartItem.listUrl === item.listUrl)) {
				this.cartPromotions.push(item);
			}

			if (!isAdminModule) {
				this.saveCartInLocalStorage();
			}
		},
		removeFromCart(item: Promotion) {
			const { isAdminModule } = useAppStore();

			this.cartPromotions = this.cartPromotions.filter((obj) => obj.listUrl !== item.listUrl);

			if (!isAdminModule) {
				this.saveCartInLocalStorage();
			}
		},
		saveCartInLocalStorage() {
			window.localStorage.setItem('cart', JSON.stringify(this.cartPromotions));
		},
		loadCartFromLocalStorage() {
			const cartFromLocalStorage = window.localStorage.getItem('cart');

			this.cartPromotions = cartFromLocalStorage ? JSON.parse(cartFromLocalStorage) : [];
		},
		togglePromotionLimitModal(value: boolean) {
			this.isLimitModalVisible = value;
		},
		scrollActivePromotionsList() {
			const activePromotionsDiv = document.getElementById('active-promotions-items');

			if (!activePromotionsDiv) {
				return;
			}

			activePromotionsDiv.scrollTop = activePromotionsDiv.scrollHeight;
		},
		togglePromotionFeedbackModal(value: boolean) {
			if (!value) {
				setTimeout(() => {
					this.isFeedbackModalVisible = value;
				}, 600);

				return;
			}

			this.isFeedbackModalVisible = value;
		},
		toggleDisableRenewModal({
			event, value, mode, id,
		} : { event?: MouseEvent, value: boolean, mode: string, id?: number }) {
			if (event) {
				event.stopPropagation();
			}

			this.selectedPromotionId = id || 0;

			if (value) {
				this.isDisableRenewModalVisible = value;
				this.isPromotionEnabled = mode === 'disable';
			} else {
				setTimeout(() => {
					this.isDisableRenewModalVisible = value;
					this.isPromotionEnabled = mode === 'disable';
				}, 600);
			}
		},
		async enablePromotions() {
			const { isAdminModule, hasFlag } = useAppStore();
			this.displayPhoneVerification = !hasFlag('phoneVerified');

			const purchases = this.promotionPackages.map((promotionPackage) => ({
				idLocation: promotionPackage.locationId,
				idCategory: promotionPackage.categoryId,
				initMonthDuration: Number(promotionPackage.months),
			}));

			this.resetFlags();

			const url = isAdminModule ? `/${this.apiNamespace}/purchase-promotions.ofer?idCustomer=${this.customerId}` : `/${this.apiNamespace}/purchase-promotions.ofer`;

			const response = await serverFetch(
				url,
				{ promotionPurchaseJSON: JSON.stringify({ purchases }) },
				'POST',
			);

			if (response.result === 'not_enough_points') {
				this.isNotEnoughPoints = true;
				await this.getAccountPoints();
				this.$router.push({ query: { 'promotion-modal': 'true' } });
				return 'NOT_ENOUGH_POINTS';
			}

			if (response.result !== 'success' && response.result !== 'unpaid_invoice' && response.result !== 'phone_verification') {
				this.isServerError = true;
				this.$router.push({ query: { 'promotion-modal': 'true' } });
				return 'SERVER_ERROR';
			}

			if (response.result === 'unpaid_invoice') {
				this.isUnpaidInvoice = true;
			}
			const phoneCheckAdmin = isAdminModule && (response.displayPhoneVerification || response.result === 'phone_verification');
			const phoneCheckCustomer = !isAdminModule && (this.displayPhoneVerification);
			if (phoneCheckAdmin || phoneCheckCustomer) {
				return 'PHONE_VERIFICATION';
			}

			this.enablePromotionsAfter();
			return '';
		},
		enablePromotionsAfter() {
			const { isAdminModule } = useAppStore();

			this.isServerError = false;

			this.enabledListsLength = this.cartPromotions.length;

			this.clearCart({ scrollEnabledList: true });

			const customerOnboarding = useCustomerOnboardingStore();
			const modalToShow = customerOnboarding.canShowModal(['promoFillProfile']);

			if (modalToShow === 'promoFillProfile' && (!this.promotionIndicator.hasGallery || !this.promotionIndicator.hasMarks || !this.promotionIndicator.hasDescription)) {
				if (!this.displayPhoneVerification) {
					setTimeout(() => {
						this.isFillProfileModalVisible = true;
					}, 5000);
				}
			}

			this.showConfirmationMessageBar = true;

			if (this.fromListBox) {
				const returnUrl = this.fromListBox === 'Rfps' ? '/konto/zapytania' : '/konto/kontakty';
				window.setTimeout(() => {
					this.fromListBox = '';
					window.location.replace(returnUrl);
				}, 300);
				return;
			}

			if (this.isSummaryPage) {
				this.$router.push({ name: 'CustomerProfilePromotion' });
			}

			if (isAdminModule) {
				document.location.href = document.location.href.replace('/podsumowanie', '').split('?')[0];
			}

			const target = document.querySelector('.active-promotions');

			if (!target) {
				return;
			}

			const { top } = target.getBoundingClientRect();

			window.setTimeout(() => {
				window.scrollTo({
					top: top - 150 > 0 ? top - 150 : 50,
					behavior: 'smooth',
				});
			}, 150);
		},
		resetFlags() {
			this.isServerError = false;
			this.isNotEnoughPoints = false;
			this.isUnpaidInvoice = false;
		},
		async getAccountPoints() {
			const { isAdminModule } = useAppStore();

			try {
				const url = isAdminModule ? `/admin/get-account-balance.ofer?customerId=${this.customerId}` : '/myoferteo/konto-json/get-account-balance.ofer';

				const { data } = await serverFetch(url);

				this.accountPoints = data.accountBalance;
			} catch (error) {
				console.error(error);
			}
		},
		async clearCart({ scrollEnabledList }: { scrollEnabledList: boolean }) {
			const { isAdminModule } = useAppStore();

			this.cartPromotions.length = 0;

			await this.getEnabledPromotions({ scrollEnabledList });

			this.clearInputs();

			if (!isAdminModule) {
				this.saveCartInLocalStorage();
			}
		},
		clearInputs() {
			this.clearCategoryInput();
			this.clearLocationInput();
		},
		clearCategoryInput() {
			this.categoryInput = '';
			this.selectedCategoryId = '';
			this.suggestions = [];

			this.clearLocationInput();
			this.getPromotionRecommendations();
		},
		clearLocationInput() {
			this.locationInput = '';
			this.selectedLocationId = '';

			if (this.categoryInput === '') {
				this.suggestions = [];
			}

			if (this.selectedCategoryId) {
				this.getSuggestions();
			} else {
				this.getPromotionRecommendations();
			}
		},
	},
	getters: {
		lowestHalfOfCartPromotions(state): Promotion[] {
			/** Sortowanie musi być takie by wybrać odpowiednią kategorię wiec sortujemy po uid. Tak samo jest na BE  */
			const sortedByUid = [...state.cartPromotions].sort((a, b) => a.uid.localeCompare(b.uid));
			const sortedByLowest = [...sortedByUid].sort((a, b) => a.price - b.price);
			const half = sortedByLowest.slice(0, Math.floor(sortedByLowest.length / 2));
			return half;
		},
		cartPropositionsSum(state): number {
			const sum = state.cartPromotions.reduce((accumulator, object) => {
				if (!object.price) {
					return accumulator;
				}
				const result = accumulator + Number(object.price);
				return result;
			}, 0);
			return sum;
		},
		discountedCartPropositionsSum(state) : number | boolean {
			const discount = state.lowestHalfOfCartPromotions.reduce((acc, obj) => (!obj.price ? acc : acc + obj.price), 0);
			const sum = state.cartPropositionsSum;
			const sumWithDiscount = sum - discount;
			return discount > 0 ? sumWithDiscount : false;
		},
		lowestHalfOfpromotionPackages(state): PromotionPackage[] {
			/** Sortowanie musi być takie by wybrać odpowiednią kategorię wiec sortujemy po uid. Tak samo jest na BE  */
			const sortedByUid = [...state.promotionPackages].sort((a, b) => a.uid.localeCompare(b.uid));
			const sortedByLowest = [...sortedByUid].sort((a, b) => a.sum - b.sum);
			const half = sortedByLowest.slice(0, Math.floor(sortedByLowest.length / 2));
			return half;
		},
		promotionPackagesSum(state): number {
			return state.promotionPackages.reduce((accumulator, object) => accumulator + (object.sum * 1), 0);
		},
		discoutendPromotionPackagesSum(state): number | boolean {
			const discount = state.lowestHalfOfpromotionPackages.reduce((acc, obj) => acc + obj.sum, 0);
			const sum = state.promotionPackagesSum;
			const sumWithDiscount = sum - discount;
			return discount > 0 ? sumWithDiscount : false;
		},
		isPromotion2in1Active: () => {
			const { hasFlag } = useAppStore();
			const isPromotionActive = hasFlag('enable_promo_promotional_campaign');
			return isPromotionActive;
		},
	},
});
